import { Component, ChangeDetectionStrategy, DestroyRef, ElementRef, OnInit, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { map } from 'rxjs';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';

import { SHARED_IMPORTS } from 'src/app/shared/standalone-imports';
import { TOMASS_SUMMARY_I18N } from './tomass-summary.i18n';
import { TomassSummaryViewModel } from './tomass-summary.viewmodel';
import type { ChartLegendItem, FrequencyRow, TomassSummaryState, UserOption } from './tomass-summary.types';
import { TomassFrequencyTableComponent } from './tomass-frequency-table';
import { TomassRegionalMapComponent } from './tomass-regional-map';

const TOMASS_DATE_FORMATS = {
  parse: { dateInput: 'YYYY/MM/DD' },
  display: {
    dateInput: 'YYYY/MM/DD',
    monthYearLabel: 'YYYY MMM',
    dateA11yLabel: 'YYYY/MM/DD',
    monthYearA11yLabel: 'YYYY MMMM',
  },
};

@Component({
  selector: 'app-tomass-summary',
  templateUrl: './tomass-summary.component.html',
  styleUrls: ['./tomass-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ...SHARED_IMPORTS,
    TomassFrequencyTableComponent,
    TomassRegionalMapComponent,
  ],
  providers: [
    TomassSummaryViewModel,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: TOMASS_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'ja-JP' },
  ],
})
export class TomassSummaryComponent implements OnInit {
  private static readonly PRINT_MODE_CLASS = 'tomass-summary-print-mode';
  private static readonly PRINT_STYLE_ID = 'tomass-summary-print-style';

  protected readonly i18n = TOMASS_SUMMARY_I18N;
  protected readonly vm = inject(TomassSummaryViewModel);
  protected readonly vm$ = this.vm.vm$;
  protected readonly userListLink = signal<readonly string[]>(['/home']);
  protected readonly LEGEND_COLLAPSED_MAX = 4;
  protected readonly expandedLegends = signal(new Set<string>());
  protected readonly selectedFrequencyDateKey = signal<string | null>(null);
  protected readonly selectedFrequencyGeohashes = signal<string[]>([]);
  @ViewChild('mapSection') private mapSectionRef?: ElementRef<HTMLElement>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);

  ngOnInit(): void {
    const routeSectionId$ = (this.route.parent?.paramMap ?? this.route.paramMap).pipe(
      map((params) => params.get('sectionId')),
    );
    routeSectionId$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((sectionId) => {
        this.userListLink.set(sectionId ? ['/home', sectionId, 'users'] : ['/home']);
      });
    this.vm.init(this.destroyRef, routeSectionId$);
  }

  onPrint(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const cleanup = this.preparePrintContext();
    const afterPrint = (): void => {
      window.removeEventListener('afterprint', afterPrint);
      cleanup();
    };
    window.addEventListener('afterprint', afterPrint);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
      });
    });

    window.setTimeout(() => {
      window.removeEventListener('afterprint', afterPrint);
      cleanup();
    }, 120000);
  }

  isPdfDisabled(state: TomassSummaryState): boolean {
    return state.status === 'loading' || !state.hasAggregated;
  }

  onStartDateChanged(date: Date | null): void {
    this.vm.updateFilters({ startDate: date });
    this.clearFrequencyRowSelection();
  }

  onEndDateChanged(date: Date | null): void {
    this.vm.updateFilters({ endDate: date });
    this.clearFrequencyRowSelection();
  }

  onAccountsChanged(userIds: string[]): void {
    this.vm.updateFilters({ selectedUserIds: userIds });
    this.clearFrequencyRowSelection();
  }

  onSelectAllAccounts(userOptions: UserOption[]): void {
    this.vm.updateFilters({ selectedUserIds: userOptions.map((o) => o.userId) });
    this.clearFrequencyRowSelection();
  }

  onDeselectAllAccounts(): void {
    this.vm.updateFilters({ selectedUserIds: [] });
    this.clearFrequencyRowSelection();
  }

  isAllAccountsSelected(selectedIds: string[], userOptions: UserOption[]): boolean {
    return userOptions.length > 0 && selectedIds.length === userOptions.length;
  }

  isAccountSelected(userId: string, selectedIds: string[]): boolean {
    return selectedIds.includes(userId);
  }

  formatAccountOptionLabel(option: UserOption): string {
    const code = option.code.trim();
    const name = option.name.trim();
    if (code) return code;
    if (name) return name;
    return option.displayName;
  }

  selectedAccountsTriggerLabel(
    selectedIds: string[],
    userOptions: UserOption[],
  ): string {
    if (selectedIds.length === 0) {
      return this.i18n.toolbar.allAccounts;
    }

    const userMap = new Map(userOptions.map((option) => [option.userId, option]));
    const labels = selectedIds
      .map((id) => userMap.get(id))
      .filter((option): option is UserOption => !!option)
      .map((option) => this.formatAccountOptionLabel(option));

    if (labels.length <= 1) {
      return labels[0] ?? this.i18n.toolbar.allAccounts;
    }
    return `${labels.length}件選択中`;
  }

  onResetDateRange(): void {
    this.vm.resetDateRange();
    this.clearFrequencyRowSelection();
  }

  onAggregate(): void {
    this.vm.aggregate();
    this.clearFrequencyRowSelection();
  }

  onFrequencyRowSelection(row: FrequencyRow | null): void {
    if (!row) {
      this.clearFrequencyRowSelection();
      return;
    }

    this.selectedFrequencyDateKey.set(row.dateKey);
    this.selectedFrequencyGeohashes.set(row.geohashes);
    this.scrollToMapOnMobile();
  }

  isPreAggregate(state: TomassSummaryState): boolean {
    return !state.hasAggregated;
  }

  isAggregateDisabled(state: TomassSummaryState): boolean {
    return state.status === 'loading' || state.filters.selectedUserIds.length === 0;
  }

  toggleLegend(key: string): void {
    const current = this.expandedLegends();
    const next = new Set(current);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.expandedLegends.set(next);
  }

  isLegendExpanded(key: string): boolean {
    return this.expandedLegends().has(key);
  }

  visibleLegendItems(items: ChartLegendItem[], key: string): ChartLegendItem[] {
    if (this.isLegendExpanded(key) || items.length <= this.LEGEND_COLLAPSED_MAX) {
      return items;
    }
    return items.slice(0, this.LEGEND_COLLAPSED_MAX);
  }

  hiddenLegendCount(items: ChartLegendItem[]): number {
    return Math.max(0, items.length - this.LEGEND_COLLAPSED_MAX);
  }

  private clearFrequencyRowSelection(): void {
    this.selectedFrequencyDateKey.set(null);
    this.selectedFrequencyGeohashes.set([]);
  }

  private scrollToMapOnMobile(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (!window.matchMedia('(max-width: 600px)').matches) {
      return;
    }

    const mapSection = this.mapSectionRef?.nativeElement;
    if (!mapSection) {
      return;
    }

    requestAnimationFrame(() => {
      mapSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    });
  }

  private preparePrintContext(): () => void {
    const body = document.body;
    const host = document.querySelector('app-tomass-summary');
    const hostElement = host instanceof HTMLElement ? host : null;
    const originalParent = hostElement?.parentElement ?? null;
    const originalNextSibling = hostElement?.nextSibling ?? null;
    const printRoot = document.createElement('div');
    printRoot.className = 'tomass-summary-print-root';

    body.classList.add(TomassSummaryComponent.PRINT_MODE_CLASS);
    if (hostElement) {
      printRoot.appendChild(hostElement);
      body.appendChild(printRoot);
    }

    document.getElementById(TomassSummaryComponent.PRINT_STYLE_ID)?.remove();

    const styleEl = document.createElement('style');
    styleEl.id = TomassSummaryComponent.PRINT_STYLE_ID;
    styleEl.textContent = `
      @media print {
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} > *:not(.tomass-summary-print-root) {
          display: none !important;
        }

        body.${TomassSummaryComponent.PRINT_MODE_CLASS} {
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
        }

        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .tomass-summary-print-root {
          display: block !important;
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .mat-sidenav-container,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .mat-drawer-container,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .mat-drawer-content,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .mat-sidenav-content,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .main-content-area,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .content,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .main_body,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .contents,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .contents_section_group,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .contents__section,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .contents--tomass-summary,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .tomass-summary-shell,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .frequency-table-wrapper,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .user-list-container {
          display: block !important;
          overflow: visible !important;
          max-height: none !important;
          height: auto !important;
          min-height: 0 !important;
        }

        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .main-content-area,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .content,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .contents--tomass-summary {
          margin: 0 !important;
          padding: 0 !important;
          border-radius: 0 !important;
        }

        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .mat-sidenav-content,
        body.${TomassSummaryComponent.PRINT_MODE_CLASS} .mat-drawer-content {
          margin-left: 0 !important;
          width: 100% !important;
        }
      }
    `;
    document.head.appendChild(styleEl);

    return () => {
      body.classList.remove(TomassSummaryComponent.PRINT_MODE_CLASS);
      document.getElementById(TomassSummaryComponent.PRINT_STYLE_ID)?.remove();
      printRoot.remove();
      if (hostElement && originalParent) {
        if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
          originalParent.insertBefore(hostElement, originalNextSibling);
        } else {
          originalParent.appendChild(hostElement);
        }
      }
    };
  }
}
